import Image from '@base/Image';
import FutureImage from '@base/FutureImage';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { segment } from '@actions/analytics';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Typography from 'aquilla/core/atoms/Typography';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
// import Button from '@base/Button/Button';
import pushRoute from '@utils/pushRoute';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { useRouter } from 'next/router';
import Button from 'aquilla/core/molecules/Button';
import useConstants from '@api/hooks/useConstants';
import { CENTRE_GOAL_PAGE, FREE_HOME_PAGE } from '@constants/segment';
import { OFFLINE_VISIT_THE_CENTRE } from './constants';

const Container = styled.div`
  border-radius: ${Spacings.SPACING_4B};
  height: 406px;
  display: flex;
  justify-content: space-around;
  margin: auto;
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_6B};
  width: 1136px;
  padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_10B};
  background-color: var(--color-i-orange-4);
  ${mediaQuery.uptoMobile} {
    height: fit-content;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_6B}
      ${Spacings.SPACING_6B} ${Spacings.SPACING_6B};
    width: 100%;
    border-radius: ${Spacings.SPACING_0B};
    border-bottom: solid ${Spacings.SPACING_2B} var(--color-violet-1);
  }
`;

const LeftDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const RightDiv = styled.div`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_8B};
`;

const SlotText = styled(Typography)`
  color: var(--color-i-orange-23);
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_1B};
`;

const StyImage = styled(FutureImage)`
  border-radius: ${Spacings.SPACING_2B};
  object-fit: cover;
`;

const LeftIncentive = styled.div`
  display: flex;
  flex-direction: column;
`;
const RightIncentive = styled.div`
  display: flex;
  flex-direction: column;
`;

const IncentivesWrapper = styled.div`
  display: flex;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const EachIncentive = styled.div`
  display: flex;
  width: 348px;
  margin-bottom: ${Spacings.SPACING_8B};
  justify-content: flex-start;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const StyAquillaButton = styled(Button)`
  width: 260px;
  height: ${Spacings.SPACING_12B};
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const IncentiveText = styled(Typography)`
  margin-left: ${Spacings.SPACING_4B};
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.LIGHT};
  white-space: pre-wrap;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
`;

const SlotsDiv = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_3B};
  width: 260px;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const StyButton = styled(Button)`
  width: 260px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const BookAVistBlock = ({ data, newGoalPage }) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const {
    setBookAVisitFlow,
    setBookAVisitClicked,
    isBookAVisitClicked,
    selectedCentreBEData
  } = useOfflineCentreStore();
  const { isBookAVisitEnabledFromCMS } = useOfflineCentreStore();

  const {
    title,
    visitCardProperties,
    ctaText,
    ctaDeepLink,
    footerText,
    thumbnailWeb
  } = data;
  const noOfIncentives = visitCardProperties.length;

  const renderLeftIncentive = () => {
    const cards = visitCardProperties
      .slice(0, Math.ceil(noOfIncentives / 2))
      .map((card) => {
        return (
          <EachIncentive key={card.id}>
            <StyImage src={card.icon} height={48} width={48} />
            <IncentiveText>{card.title}</IncentiveText>
          </EachIncentive>
        );
      });
    return cards;
  };
  const renderRightIncentive = () => {
    const cards = visitCardProperties
      .slice(
        noOfIncentives % 2 === 0
          ? Math.ceil(noOfIncentives / 2)
          : Math.ceil(noOfIncentives / 2) + 1
      )
      .map((card) => {
        return (
          <EachIncentive key={card.id}>
            <StyImage src={card.icon} height={48} width={48} />
            <IncentiveText>{card.title}</IncentiveText>
          </EachIncentive>
        );
      });
    return cards;
  };
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);

  const { centreName: cmsCentreName, cityName: cmsCityName } = centreInfo || {};
  const { goalInfo: goalData = {} } = useGoalInfo(routeGoalUID);

  const { city, state, name: centreName } = selectedCentreBEData || {};
  const handleBookAVisitClick = () => {
    segment.track(OFFLINE_VISIT_THE_CENTRE, {
      city: city || cmsCityName,
      state,
      centre_name: centreName || cmsCentreName,
      goal_name: goalData?.name,
      goal_uid: routeGoalUID,
      block_position: 'Bottom',
      last_primary_source: newGoalPage ? FREE_HOME_PAGE : CENTRE_GOAL_PAGE
    });
    if (!isLoggedIn) {
      openLoginDrawer();
      if (isBookAVisitEnabledFromCMS || newGoalPage) setBookAVisitClicked(true);
    } else if (isBookAVisitEnabledFromCMS || newGoalPage) {
      setBookAVisitFlow(true);
    } else window.open(ctaDeepLink);
  };

  const { selectedGoal } = useSelectedGoal();

  const handleAllCentreRouting = () => {
    const eligibleGoals = ['TMUVD', 'YOTUH', 'TPSBK', 'XFVVT'];
    if (selectedGoal && eligibleGoals.includes(selectedGoal?.uid)) {
      pushRoute({
        pathname: `/all-centre/${selectedGoal?.uid}`
      });
    } else {
      pushRoute({
        pathname: '/all-centre'
      });
    }
  };

  const visitCentrePage = () => {
    handleAllCentreRouting();
  };
  const { constants } = useConstants();

  const showButton = () => {
    if (constants?.ENABLE_CENTRES_PAGE) {
      return (
        <StyAquillaButton
          label="Know more about centres"
          variant="secondary"
          onClick={visitCentrePage}
          size="large"
        />
      );
    }
  };
  useEffect(() => {
    if (isBookAVisitClicked && isLoggedIn) {
      setBookAVisitClicked(false);
      setBookAVisitFlow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  if (!isBookAVisitEnabledFromCMS && !newGoalPage) return null;
  return (
    <Container>
      <LeftDiv>
        <Title variant="h2"> {title}</Title>
        <IncentivesWrapper>
          <LeftIncentive>{renderLeftIncentive()}</LeftIncentive>
          <RightIncentive>{renderRightIncentive()}</RightIncentive>
        </IncentivesWrapper>
        <ButtonsContainer>
          <StyButton
            label={ctaText}
            color="black"
            onClick={handleBookAVisitClick}
            size="large"
          />
          {showButton()}
        </ButtonsContainer>
        <SlotsDiv>
          <Image
            src="offline-centres/slotsFillingFast.svg"
            height={18}
            width={18}
            alt="slots-fillng-fast"
          />
          <SlotText variant="p2">{footerText}</SlotText>
        </SlotsDiv>
      </LeftDiv>
      <RightDiv>
        <StyImage src={thumbnailWeb} height={310} width={329} />
      </RightDiv>
    </Container>
  );
};

export default BookAVistBlock;
